import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Upload, FileText, Trash2, Download, X, Loader2, Plus,
} from "lucide-react";

const DEFAULT_CATEGORIES = ["Fiscalité", "Comptabilité", "Paie & RH", "Juridique", "Actualités", "Autre"];
const ACCEPT_MIME = ".pdf,.doc,.docx,.xls,.xlsx,.zip,.png,.jpg,.jpeg,.txt,.csv";

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} Mo`;
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

const UploadModal = ({ onClose, onUploaded, board, categories, testIdPrefix }) => {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(categories[0]);
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Sélectionnez un fichier");
    if (title.trim().length < 2) return toast.error("Titre trop court");

    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title.trim());
    fd.append("category", category);
    fd.append("board", board);
    if (description) fd.append("description", description);

    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Document « ${data.title} » publié`);
      onUploaded(data);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Échec du téléchargement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid={`${testIdPrefix}-upload-modal`}
      className="fixed inset-0 z-[70] bg-teal-dark/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-lg border border-ink-200"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200">
          <div>
            <p className="eyebrow text-teal-accent">{board === "recruitment" ? "Recrutement" : "Publier"}</p>
            <h3 className="font-serif text-2xl text-teal-dark mt-1">Nouveau document</h3>
          </div>
          <button type="button" onClick={onClose} className="text-ink-500 hover:text-teal-dark" data-testid={`${testIdPrefix}-upload-modal-close`}>
            <X size={22} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <label className="block">
            <span className="eyebrow text-ink-500 block mb-2">Titre *</span>
            <input
              data-testid="upload-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required minLength={2}
              placeholder="Ex : Actualités fiscales décembre 2026"
              className="w-full border border-ink-200 px-4 py-3 focus:border-teal-accent focus:outline-none focus:ring-2 focus:ring-teal-accent/20"
            />
          </label>

          <label className="block">
            <span className="eyebrow text-ink-500 block mb-2">Catégorie</span>
            <select
              data-testid={`${testIdPrefix}-upload-category`}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full border border-ink-200 px-4 py-3 bg-white focus:border-teal-accent focus:outline-none focus:ring-2 focus:ring-teal-accent/20"
            >
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="eyebrow text-ink-500 block mb-2">Description (facultative)</span>
            <textarea
              data-testid="upload-description"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-ink-200 px-4 py-3 focus:border-teal-accent focus:outline-none focus:ring-2 focus:ring-teal-accent/20 resize-none"
            />
          </label>

          <div>
            <span className="eyebrow text-ink-500 block mb-2">Fichier * (max 15 Mo)</span>
            <input
              ref={inputRef}
              data-testid="upload-file"
              type="file"
              accept={ACCEPT_MIME}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="w-full border border-dashed border-teal-dark/40 hover:border-teal-accent p-6 text-left group transition-colors"
              data-testid="upload-file-select"
            >
              {file ? (
                <div className="flex items-center gap-3">
                  <FileText size={22} className="text-teal-accent" />
                  <div>
                    <p className="text-ink-800 text-sm">{file.name}</p>
                    <p className="text-xs text-ink-500">{formatSize(file.size)}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 text-ink-500">
                  <Upload size={22} className="text-teal-accent" />
                  <span className="text-sm">Cliquer pour sélectionner un fichier</span>
                </div>
              )}
            </button>
            <p className="mt-2 text-xs text-ink-500">Formats : PDF, DOC(X), XLS(X), ZIP, PNG, JPG, TXT, CSV.</p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-ink-200 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-sm text-ink-500 hover:text-teal-dark px-4">Annuler</button>
          <button
            type="submit"
            data-testid="upload-submit"
            disabled={loading}
            className="btn-primary text-sm disabled:opacity-60"
          >
            {loading ? (<><Loader2 size={16} className="animate-spin" /> Envoi…</>) : (<><Upload size={16} /> Publier</>)}
          </button>
        </div>
      </form>
    </div>
  );
};

const NewsletterPanel = ({
  isAdmin,
  board = "newsletter",
  listUrl = "/newsletter/documents",
  categories = DEFAULT_CATEGORIES,
  eyebrow = "Publications du cabinet",
  title = "Newsletter & documents",
  description = "Retrouvez ici les publications, notes d'actualité et documents partagés par le cabinet.",
  emptyTitle = "Aucune publication pour le moment",
  emptyHint,
  addLabel = "Ajouter un document",
  testIdPrefix = "newsletter",
}) => {
  const [docs, setDocs] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [filter, setFilter] = useState("Tous");

  const load = async () => {
    try {
      const { data } = await api.get(listUrl);
      setDocs(data);
    } catch {
      setDocs([]);
      toast.error("Impossible de charger les documents");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [listUrl]);

  const allCategories = useMemo(() => {
    const s = new Set(["Tous"]);
    (docs || []).forEach((d) => s.add(d.category || "Général"));
    return Array.from(s);
  }, [docs]);

  const visible = useMemo(() => {
    if (!docs) return [];
    if (filter === "Tous") return docs;
    return docs.filter((d) => (d.category || "Général") === filter);
  }, [docs, filter]);

  const doDownload = async (doc) => {
    try {
      const res = await api.get(`/newsletter/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || doc.title;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement impossible");
    }
  };

  const doDelete = async (doc) => {
    if (!window.confirm(`Supprimer « ${doc.title} » ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/newsletter/documents/${doc.id}`);
      toast.success("Document supprimé");
      setDocs((d) => d.filter((x) => x.id !== doc.id));
    } catch {
      toast.error("Suppression impossible");
    }
  };

  return (
    <div className="border border-ink-200 bg-white" data-testid={`${testIdPrefix}-panel`}>
      <div className="p-6 border-b border-ink-200 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow text-ink-500">{eyebrow}</p>
          <p className="font-serif text-2xl md:text-3xl text-teal-dark mt-1">{title}</p>
          <p className="text-sm text-ink-500 mt-2 max-w-lg">{description}</p>
        </div>
        {isAdmin && (
          <button
            data-testid={`${testIdPrefix}-upload-btn`}
            onClick={() => setShowUpload(true)}
            className="btn-primary text-sm"
          >
            <Plus size={16} /> {addLabel}
          </button>
        )}
      </div>

      {docs === null ? (
        <div className="p-10 text-ink-500 text-sm">Chargement…</div>
      ) : docs.length === 0 ? (
        <div className="p-14 text-center">
          <FileText size={36} className="mx-auto text-teal-accent" />
          <p className="font-serif text-2xl text-teal-dark mt-4">{emptyTitle}</p>
          <p className="text-ink-500 text-sm mt-2">
            {isAdmin ? "Ajoutez votre premier document ci-dessus." : (emptyHint || "Votre cabinet publiera bientôt du contenu ici.")}
          </p>
        </div>
      ) : (
        <>
          {allCategories.length > 2 && (
            <div className="px-6 pt-5 flex flex-wrap gap-2" data-testid={`${testIdPrefix}-filters`}>
              {allCategories.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  className={`text-xs eyebrow px-3 py-1.5 border transition-colors ${
                    filter === c
                      ? "bg-teal-dark text-white border-teal-dark"
                      : "border-ink-200 text-ink-500 hover:border-teal-dark hover:text-teal-dark"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          <ul className="divide-y divide-ink-200">
            {visible.map((doc) => (
              <li
                key={doc.id}
                data-testid={`${testIdPrefix}-doc-${doc.id}`}
                className="p-6 flex flex-col md:flex-row md:items-center gap-4 hover:bg-teal-l1/40 transition-colors"
              >
                <div className="bg-teal-l1 p-3 shrink-0">
                  <FileText size={22} className="text-teal-dark" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-3">
                    <h4 className="font-serif text-xl text-teal-dark leading-tight truncate">{doc.title}</h4>
                    <span className="text-xs eyebrow text-teal-accent bg-teal-l1 px-2 py-0.5">{doc.category || "Général"}</span>
                  </div>
                  {doc.description && (
                    <p className="text-sm text-ink-500 mt-1 line-clamp-2">{doc.description}</p>
                  )}
                  <p className="text-xs text-ink-500 mt-2">
                    {doc.original_filename} · {formatSize(doc.size)} · Publié le {formatDate(doc.created_at)}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => doDownload(doc)}
                    data-testid={`${testIdPrefix}-download-${doc.id}`}
                    className="inline-flex items-center gap-2 text-sm text-teal-dark border border-teal-dark px-3 py-2 hover:bg-teal-l1"
                  >
                    <Download size={14} /> Télécharger
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => doDelete(doc)}
                      data-testid={`${testIdPrefix}-delete-${doc.id}`}
                      className="inline-flex items-center gap-2 text-sm text-red-700 border border-red-200 px-3 py-2 hover:bg-red-50"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onUploaded={(doc) => setDocs((d) => [doc, ...(d || [])])}
          board={board}
          categories={categories}
          testIdPrefix={testIdPrefix}
        />
      )}
    </div>
  );
};

export default NewsletterPanel;
